/**
 * InlineVideoThumb — サムネタップでインライン再生する動画サムネ要素
 *
 * # 仕様
 * - 基本的にサムネイル画像 ＋ VideoPlayOverlay（再生アイコン）を表示する。
 * - タップすると <video>（自動再生）へ差し替わる。動画への初回タップで
 *   コントロールが出る（短尺動画でオーバーレイが目障りにならないように）。
 * - スレッド（ページ）内で同時に1個しか video 化しない。別のサムネをタップ
 *   すると前の動画はサムネへ戻る。
 * - 再生中のままページ遷移などで消えると自動で解除されるので、外側にリセット
 *   処理は不要。一覧が再生成されると識別子（useId）も更新され、遷移して戻った
 *   際の誤自動再生も起きない。
 *
 * # 設置方法
 * 動画URL・サムネ画像URL・表示サイズを渡すだけ。識別子（post.id 等）は内部の
 * useId で自動採番するため不要。
 *
 * ```tsx
 * import { InlineVideoThumb } from "@/shared/ui/media";
 *
 * <InlineVideoThumb
 *   src={動画URL}
 *   thumbnailSrc={サムネ画像URL}
 *   width={表示幅}
 *   height={表示高さ}
 * />
 * ```
 *
 * 依存: 同フォルダの VideoPlayOverlay（react-icons）と Tailwind クラス。
 * 他サイトへ移す場合はこの2点も併せて持っていくこと。
 */
import { cn } from "@/shared/lib/cn";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  type ToggleEvent,
  useTogglePresenter,
} from "@/shared/ui/component-hierarchy/useTogglePresenter";
import { useInlineVideoPresenter } from "@/shared/ui/media/useInlineVideoPresenter";
import { VideoPlayOverlay } from "@/shared/ui/media/VideoPlayOverlay";

interface InlineVideoThumbProps {
  /** 動画URL */
  src: string;
  /** サムネイル画像URL */
  thumbnailSrc: string;
  width: number;
  height: number;
  alt?: string;
  /** 初期表示のLCP候補としてサムネイル取得を優先する。 */
  priority?: boolean;
  /** 外側要素のクラスを拡張する場合に指定 */
  className?: string;
}

/** 本体。仕様・使い方はファイル冒頭の説明を参照。 */
export const InlineVideoThumb: React.FunctionComponent<
  InlineVideoThumbProps
> = (props: InlineVideoThumbProps) => {
  const presentation = useInlineVideoPresenter();
  return (
    <ComponentScope node={presentation.node}>
      <InlineVideoThumbView
        {...props}
        playing={presentation.playing}
        onPlay={presentation.onPlay}
      />
    </ComponentScope>
  );
};

interface InlineVideoThumbViewProps extends InlineVideoThumbProps {
  playing: boolean;
  onPlay: () => void;
}

export const InlineVideoThumbView: React.FunctionComponent<
  InlineVideoThumbViewProps
> = ({
  src,
  thumbnailSrc,
  width,
  height,
  alt = "",
  priority = false,
  className,
  playing,
  onPlay,
}: InlineVideoThumbViewProps) => {
  if (playing) {
    return <Player src={src} width={width} height={height} />;
  }

  return (
    <button
      type="button"
      onClick={onPlay}
      className={cn("relative inline-block", className)}
      aria-label="動画を再生"
    >
      {/* Tailwind v4 preflight は img { height: auto } を強制するため、
          height 属性だけでは無効化できない（Issue #121 レビュー指摘）。
          inline style で width/height を明示し preflight を上書きする。
          object-contain により元画像とのアスペクト比差分はレターボックスで吸収する。 */}
      <img
        src={thumbnailSrc}
        alt={alt}
        className="max-w-full rounded-lg object-contain"
        width={width}
        height={height}
        style={{ width, height }}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
      />
      <VideoPlayOverlay />
    </button>
  );
};

interface PlayerProps {
  src: string;
  width: number;
  height: number;
}

/**
 * 再生中の <video>。短尺動画ではコントロールのオーバーレイが目障りなため、
 * 初期は controls なしで自動再生し、動画への初回タップで controls を表示する。
 * 再生対象が切り替わる度に新規マウントされるので showControls は自然にリセットされる。
 */
const Player: React.FunctionComponent<PlayerProps> = (props: PlayerProps) => {
  const presentation = useTogglePresenter(false);
  return (
    <ComponentScope node={presentation.node}>
      <PlayerView
        {...props}
        showControls={presentation.pressed}
        onEvent={presentation.onEvent}
      />
    </ComponentScope>
  );
};

interface PlayerViewProps extends PlayerProps {
  showControls: boolean;
  onEvent: (event: ToggleEvent) => void;
}
const PlayerView: React.FunctionComponent<PlayerViewProps> = ({
  src,
  width,
  height,
  showControls,
  onEvent,
}: PlayerViewProps) => {
  return (
    // Tailwind v4 preflight は video { height: auto } を強制するため、
    // height 属性だけでは無効化できない（Issue #121 レビュー指摘）。
    // inline style で width/height を明示し preflight を上書きする。
    <video
      src={src}
      className="max-w-full rounded-lg object-contain"
      width={width}
      height={height}
      style={{ width, height }}
      controls={showControls}
      autoPlay
      preload="none"
      playsInline
      onClick={(): void =>
        onEvent({ type: "view-toggle.requested", value: true })
      }
    >
      <track kind="captions" />
    </video>
  );
};
